import { useQuery } from '@tanstack/react-query';
import { browserExtensionClient } from 'clients/BrowserExtensionClient/BrowserExtensionClient';
import { QueryKey } from 'types/query';
import { useIndeedConfig } from './useIndeedConfig';

export type IndeedProject = {
  default: boolean;
  key: string;
  name: string;
};

type IndeedProjectListGQLMutationResponse = {
  findTalentProjectBriefs: {
    projects: IndeedProject[];
  };
};

type GetProjectListGQLQueryHandler = (params: {
  headers: Record<string, any> | undefined;
}) => Promise<IndeedProjectListGQLMutationResponse>;

type IndeedProjectRest = {
  default: boolean;
  projectKey: string;
  projectName: string;
};

type GetProjectListRestHandler = () => Promise<{ results: IndeedProjectRest[] }>;

export const useIndeedProjectListQuery = () => {
  const indeedConfig = useIndeedConfig();

  return useQuery(
    [QueryKey.useIndeedProjectListQuery],
    async (): Promise<IndeedProject[]> => {
      if (!indeedConfig.isLoggedIn) return [];

      try {
        const response = await getProjectsList({ headers: indeedConfig.headers });

        return response.findTalentProjectBriefs.projects;
      } catch {
        const { results } = await getProjectsListRest();

        return results.map((project) => ({
          default: project.default,
          key: project.projectKey,
          name: project.projectName,
        }));
      }
    },
    { enabled: indeedConfig.isLoggedIn, retry: 0, initialData: [], initialDataUpdatedAt: 0 },
  );
};

const findTalentProjectBriefsGQLQuery = () => ({
  operationName: 'findTalentProjectBriefs',
  variables: {
    input: {},
  },
  query:
    'query findTalentProjectBriefs($input: FindTalentProjectBriefsInput!) {\n  findTalentProjectBriefs(input: $input) {\n    projects {\n      default\n      key\n      name\n      createdByAccountKey\n      __typename\n    }\n    __typename\n  }\n}\n',
});

const getProjectsListRest: GetProjectListRestHandler = async () => {
  const response = await browserExtensionClient.sendMessage('proxyHttpRequest', {
    url: 'https://resumes.indeed.com/api/v1/hiring/projects/?limit=100',
    method: 'GET',
  });

  return JSON.parse(response.body);
};

const getProjectsList: GetProjectListGQLQueryHandler = async ({ headers }) => {
  const response = await browserExtensionClient.sendMessage('proxyHttpRequest', {
    url: 'https://apis.indeed.com/graphql',
    method: 'POST',
    body: JSON.stringify(findTalentProjectBriefsGQLQuery()),
    headers,
  });

  return JSON.parse(response.body).data;
};
